export type HeicErrorCode =
  | 'unsupported_type'
  | 'too_large'
  | 'too_many_files'
  | 'unsupported_webp'
  | 'corrupt_image'
  | 'canvas_error'
  | 'conversion_failed'
  | 'cancelled'

export interface HeicErrors {
  unsupported: string
  tooLarge: string
  tooMany?: string
  unsupportedWebp?: string
  corrupt: string
  canvas?: string
  conversion: string
  generic: string
}

export type HeicOutputFormat = 'jpg' | 'png' | 'webp'

export interface HeicConverterConfig {
  utilityKey: 'heic-to-jpg' | 'heic-to-png' | 'heic-to-webp'
  outputExtension: HeicOutputFormat
  outputMimeType: 'image/jpeg' | 'image/png' | 'image/webp'
  outputLabel: string
  showQuality: boolean
  defaultQuality?: number
  zipFilename: string
}

export interface HeicDecoderOptions {
  blob: Blob
  toType: HeicConverterConfig['outputMimeType']
  quality?: number
  multiple: true
}

export type HeicDecoder = (
  options: HeicDecoderOptions,
) => Promise<Blob | Blob[]>

export const MAX_HEIC_FILE_SIZE = 50 * 1024 * 1024
export const MAX_HEIC_BATCH_FILES = 10

const ACCEPTED_EXTENSIONS = ['.heic', '.heif']
const ACCEPTED_TYPES = [
  'image/heic',
  'image/heif',
  'image/heic-sequence',
  'image/heif-sequence',
]

let webpEncodeSupport: Promise<boolean> | null = null

const OUTPUT_CONFIGS = {
  jpg: {
    utilityKey: 'heic-to-jpg',
    outputExtension: 'jpg',
    outputMimeType: 'image/jpeg',
    outputLabel: 'JPG',
    showQuality: true,
    defaultQuality: 85,
    zipFilename: 'heic-to-jpg.zip',
  },
  png: {
    utilityKey: 'heic-to-png',
    outputExtension: 'png',
    outputMimeType: 'image/png',
    outputLabel: 'PNG',
    showQuality: false,
    zipFilename: 'heic-to-png.zip',
  },
  webp: {
    utilityKey: 'heic-to-webp',
    outputExtension: 'webp',
    outputMimeType: 'image/webp',
    outputLabel: 'WebP',
    showQuality: true,
    defaultQuality: 82,
    zipFilename: 'heic-to-webp.zip',
  },
} as const satisfies Record<HeicOutputFormat, HeicConverterConfig>

export function heicOutputConfig(format: HeicOutputFormat): HeicConverterConfig {
  return OUTPUT_CONFIGS[format]
}

export function boundedHeicOutput(value: unknown): HeicOutputFormat {
  return value === 'png' || value === 'webp' ? value : 'jpg'
}

export function heicAnalyticsDimensions(
  format: HeicOutputFormat,
): { output_format: HeicOutputFormat } {
  return { output_format: format }
}

export function validateHeicOutputBlob(
  blob: Blob | null | undefined,
  expectedMimeType: HeicConverterConfig['outputMimeType'],
  errors: HeicErrors,
): Blob {
  if (!blob || blob.type !== expectedMimeType) {
    throw new HeicConversionError(errors.conversion, 'conversion_failed')
  }
  return blob
}

export class HeicConversionError extends Error {
  constructor(
    message: string,
    readonly code: HeicErrorCode,
  ) {
    super(message)
  }
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw new HeicConversionError('Conversion cancelled.', 'cancelled')
  }
}

export function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

export function isHeicLike(file: File): boolean {
  const type = file.type.toLowerCase()
  const name = file.name.toLowerCase()
  return (
    ACCEPTED_TYPES.includes(type) ||
    ACCEPTED_EXTENSIONS.some((extension) => name.endsWith(extension))
  )
}

export function outputFilename(inputName: string, extension: string): string {
  const base = inputName.replace(/\.[^.]+$/, '')
  return `${base || 'converted'}.${extension}`
}

export function uniqueOutputFilename(
  inputName: string,
  extension: string,
  usedNames: Set<string>,
): string {
  const filename = outputFilename(inputName, extension)
  if (!usedNames.has(filename)) {
    usedNames.add(filename)
    return filename
  }

  const base = filename.replace(new RegExp(`\\.${extension}$`, 'i'), '')
  let counter = 2
  let next = `${base}-${counter}.${extension}`
  while (usedNames.has(next)) {
    counter += 1
    next = `${base}-${counter}.${extension}`
  }
  usedNames.add(next)
  return next
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export function validateHeicFile(
  file: File,
  errors: HeicErrors,
): { message: string; code: HeicErrorCode } | null {
  if (!isHeicLike(file)) {
    return { message: errors.unsupported, code: 'unsupported_type' }
  }
  if (file.size > MAX_HEIC_FILE_SIZE) {
    return { message: errors.tooLarge, code: 'too_large' }
  }
  return null
}

export async function validateHeicContainer(
  file: File,
  errors: HeicErrors,
  signal?: AbortSignal,
): Promise<void> {
  throwIfAborted(signal)
  const bytes = new Uint8Array(await file.slice(0, 64).arrayBuffer())
  throwIfAborted(signal)
  if (
    bytes.length < 16 ||
    String.fromCharCode(...bytes.subarray(4, 8)) !== 'ftyp'
  ) {
    throw new HeicConversionError(errors.corrupt, 'corrupt_image')
  }

  const declaredSize = new DataView(
    bytes.buffer,
    bytes.byteOffset,
    bytes.byteLength,
  ).getUint32(0)
  if (declaredSize < 16 || declaredSize > file.size) {
    throw new HeicConversionError(errors.corrupt, 'corrupt_image')
  }

  const brands = new Set<string>([
    String.fromCharCode(...bytes.subarray(8, 12)),
  ])
  for (let offset = 16; offset + 4 <= Math.min(bytes.length, declaredSize); offset += 4) {
    brands.add(String.fromCharCode(...bytes.subarray(offset, offset + 4)))
  }
  if (![...brands].some((brand) =>
    brand === 'heic' ||
    brand === 'heix' ||
    brand === 'hevc' ||
    brand === 'hevx'
  )) {
    throw new HeicConversionError(errors.unsupported, 'unsupported_type')
  }
}

async function loadHeicDecoder(): Promise<HeicDecoder> {
  const { default: decoder } = await import('heic2any')
  return decoder as HeicDecoder
}

function loadImage(src: string, errors: HeicErrors): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new HeicConversionError(errors.corrupt, 'corrupt_image'))
    img.src = src
  })
}

function supportsWebpEncode(): Promise<boolean> {
  if (!webpEncodeSupport) {
    webpEncodeSupport = new Promise((resolve) => {
      const canvas = document.createElement('canvas')
      canvas.width = 1
      canvas.height = 1
      if (!canvas.toBlob) {
        resolve(false)
        return
      }
      canvas.toBlob((blob) => resolve(blob?.type === 'image/webp'), 'image/webp', 0.8)
    })
  }
  return webpEncodeSupport
}

export function encodeImageBlob(
  source: Blob,
  mimeType: 'image/jpeg' | 'image/png' | 'image/webp',
  quality: number | undefined,
  errors: HeicErrors,
  signal?: AbortSignal,
): Promise<Blob> {
  throwIfAborted(signal)
  const sourceUrl = URL.createObjectURL(source)

  return loadImage(sourceUrl, errors)
    .then((img) => {
      throwIfAborted(signal)
      return encodeLoadedImage(img, mimeType, quality, errors, signal)
    })
    .finally(() => URL.revokeObjectURL(sourceUrl))
}

function encodeLoadedImage(
  img: HTMLImageElement,
  mimeType: 'image/jpeg' | 'image/png' | 'image/webp',
  quality: number | undefined,
  errors: HeicErrors,
  signal?: AbortSignal,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas')
    canvas.width = img.naturalWidth
    canvas.height = img.naturalHeight

    const ctx = canvas.getContext('2d')
    if (!ctx) {
      reject(new HeicConversionError(errors.canvas ?? errors.conversion, 'canvas_error'))
      return
    }

    if (mimeType === 'image/jpeg') {
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
    }
    ctx.drawImage(img, 0, 0)

    canvas.toBlob(
      (blob) => {
        canvas.width = 0
        canvas.height = 0
        if (signal?.aborted) {
          reject(new HeicConversionError('Conversion cancelled.', 'cancelled'))
          return
        }
        try {
          resolve(validateHeicOutputBlob(blob, mimeType, errors))
        } catch (error) {
          reject(error)
        }
      },
      mimeType,
      quality,
    )
  })
}

async function encodeImageToWebp(
  img: HTMLImageElement,
  quality: number,
  errors: HeicErrors,
  signal?: AbortSignal,
): Promise<Blob> {
  const blob = await encodeLoadedImage(img, 'image/webp', quality / 100, errors, signal)
  if (blob.type !== 'image/webp') {
    throw new HeicConversionError(
      errors.unsupportedWebp ?? errors.conversion,
      'unsupported_webp',
    )
  }
  return blob
}

export async function decodeHeicToPng(
  file: File,
  errors: HeicErrors,
  signal?: AbortSignal,
  decoder?: HeicDecoder,
): Promise<{ blob: Blob; frames: number }> {
  try {
    await validateHeicContainer(file, errors, signal)
    const heic2any = decoder ?? await loadHeicDecoder()
    throwIfAborted(signal)
    const decoded = await heic2any({
      blob: file,
      toType: 'image/png',
      multiple: true,
    })
    const frames = Array.isArray(decoded) ? decoded.length : 1
    const blob = Array.isArray(decoded) ? decoded[0] : decoded

    throwIfAborted(signal)
    const validatedBlob = validateHeicOutputBlob(blob, 'image/png', errors)

    return { blob: validatedBlob, frames }
  } catch (error) {
    if (error instanceof HeicConversionError) {
      throw error
    }
    throw new HeicConversionError(errors.corrupt, 'corrupt_image')
  }
}

export async function convertHeic(
  file: File,
  config: HeicConverterConfig,
  quality: number,
  errors: HeicErrors,
  signal?: AbortSignal,
  decoder?: HeicDecoder,
): Promise<{ blob: Blob; frames: number }> {
  throwIfAborted(signal)
  if (config.outputMimeType === 'image/webp') {
    const canEncodeWebp = await supportsWebpEncode()
    if (!canEncodeWebp) {
      throw new HeicConversionError(
        errors.unsupportedWebp ?? errors.conversion,
        'unsupported_webp',
      )
    }

    const { blob: decodedBlob, frames } = await decodeHeicToPng(
      file,
      errors,
      signal,
      decoder,
    )
    const decodedUrl = URL.createObjectURL(decodedBlob)
    try {
      const img = await loadImage(decodedUrl, errors)
      throwIfAborted(signal)
      const blob = await encodeImageToWebp(img, quality, errors, signal)
      return { blob, frames }
    } catch (error) {
      if (error instanceof HeicConversionError) {
        throw error
      }
      throw new HeicConversionError(errors.conversion, 'conversion_failed')
    } finally {
      URL.revokeObjectURL(decodedUrl)
    }
  }

  try {
    await validateHeicContainer(file, errors, signal)
    const heic2any = decoder ?? await loadHeicDecoder()
    throwIfAborted(signal)
    const result = await heic2any({
      blob: file,
      toType: config.outputMimeType,
      quality: config.showQuality ? quality / 100 : undefined,
      multiple: true,
    })
    const frames = Array.isArray(result) ? result.length : 1
    const blob = Array.isArray(result) ? result[0] : result

    throwIfAborted(signal)
    const validatedBlob = validateHeicOutputBlob(blob, config.outputMimeType, errors)

    return { blob: validatedBlob, frames }
  } catch (error) {
    if (error instanceof HeicConversionError) {
      throw error
    }
    throw new HeicConversionError(errors.corrupt, 'corrupt_image')
  }
}
